import React, { useState, useEffect } from 'react';
import { Newspaper, ExternalLink, Clock } from 'lucide-react';
import { USER_ROLES } from '../utils/roles';

export default function WatchlistNewsFeed({ user, userRole }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    if (user) {
      fetchWatchlistNews();
    }
  }, [user]);

  const fetchWatchlistNews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/news/watchlist/${user.uid}?limit=5`);
      const data = await response.json();

      if (data.success) {
        setNews(data.news);
      } else {
        setError('뉴스를 불러올 수 없습니다');
      }
    } catch (err) {
      setError('서버 연결 실패');
      console.error('뉴스 로딩 에러:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="text-center text-gray-500 py-4 text-sm">
        로그인하여 관심 종목의 최신 뉴스를 확인하세요
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="text-gray-500 text-sm">뉴스를 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center">
        <p className="text-red-600 text-sm mb-2">{error}</p>
        <button
          onClick={fetchWatchlistNews}
          className="text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {news.length === 0 ? (
        <div className="text-center text-gray-500 py-4 text-sm">
          관심 종목의 뉴스가 없습니다
        </div>
      ) : (
        news.map((item) => (
          <article
            key={item.id}
            className="border-b border-gray-100 pb-3 last:border-0 hover:bg-gray-50 p-2 rounded transition-colors"
          >
            <div className="flex items-start gap-2">
              <Newspaper className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />

              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1 text-gray-900 line-clamp-2">
                  {item.title}
                </h4>

                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                    {item.ticker}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(item.publishedAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                  </span>
                  {item.source && (
                    <span 
                      className="flex items-center gap-1"
                      style={{
                        color: item.isOfficialSource ? '#10b981' : '#9ca3af',
                        fontWeight: item.isOfficialSource ? '600' : '400'
                      }}
                    >
                      {item.isOfficialSource && '🏢'}
                      {item.source}
                    </span>
                  )}
                </div>

                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-medium"
                >
                  원문 보기
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </article>
        ))
      )}

      {news.length > 0 && userRole === USER_ROLES.ADMIN && (
        <button
          onClick={fetchWatchlistNews}
          className="w-full text-xs text-blue-600 hover:text-blue-700 pt-2"
        >
          새로고침
        </button>
      )}
    </div>
  );
}
